import {useCallback, useEffect, useState} from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {ID, isNotEmpty, KTSVG} from '../../../../_metronic/helpers'
import {Toast} from '../../../../_metronic/partials/widgets'
import {getServerErrorMessage} from '../../../../_metronic/helpers/apiError'
import {
  getTaskAttachmentError,
  TASK_ATTACHMENT_ACCEPT,
} from '../../../../_metronic/helpers/taskAttachment'
import {createAttachement, deleteAttachment} from '../core/_requests'
import {Attachment} from '../core/_models'

type Props = {
  attachments?: Attachment[]
  templateId: ID
}

export const Attachement = ({attachments = [], templateId}: Props) => {
  const intl = useIntl()
  const [localAttachments, setLocalAttachments] = useState(attachments)
  const queryClient = useQueryClient()

  const initialValues = {
    file: '',
    template_id: undefined,
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.file)) {
          await createAttachement(values).then((response: any) => {
            Toast.fire({
              icon: 'success',
              title: intl.formatMessage(
                {id: 'ALERT.LABEL.SUCCESS_CREATE_ITEM'},
                {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
              ),
              customClass: {
                popup: 'bg-light-success',
                title: 'text-success',
              },
            })
            setLocalAttachments((prev) => [...prev, response.data])
            queryClient.invalidateQueries()
            resetForm()
          })
        }
      } catch (ex) {
        console.error(ex)
        Toast.fire({
          icon: 'error',
          title: getServerErrorMessage(ex) ?? intl.formatMessage({id: 'ALERT.LABEL.ERROR'}),
          timer: 8000,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      } finally {
        setSubmitting(true)
      }
    },
  })

  useEffect(() => {
    formik.setFieldValue('template_id', templateId)
  }, [])

  const handleCreate = useCallback(
    async (e: any) => {
      // Mesma regra do servidor (tipo e 20MB): recusa na seleção, sem enviar.
      const attachmentError = e.target.files?.[0] ? getTaskAttachmentError(e.target.files[0]) : null
      if (attachmentError) {
        Toast.fire({
          icon: 'error',
          title: attachmentError,
          timer: 8000,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
        e.target.value = null
        return
      }
      formik.setFieldValue('file', e.target.files[0])
      formik.submitForm()
      e.target.value = null
      formik.setFieldValue('template_id', templateId)
    },
    [formik]
  )

  const deleta = async (id: ID) => {
    const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
    const tema = CurrentTheme === 'dark' ? '#353b48' : '#fff'

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-danger',
      },
      buttonsStyling: false,
    })

    swalWithBootstrapButtons
      .fire({
        title: intl.formatMessage({id: 'ALERT.LABEL.ARE_YOU_SURE'}),
        html: intl.formatMessage({id: 'ALERT.LABEL.IT_CAN_NOT_BE_UNDONE'}),
        icon: 'warning',
        background: tema,
        showCancelButton: true,
        confirmButtonText: 'Sim, pode excluir',
        cancelButtonText: 'Não, cancele!',
        reverseButtons: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          return await deleteItem.mutateAsync(id)
        } else if (
          // @ts-ignore
          result.dismiss === Swal.DismissReason.cancel
        ) {
          Toast.fire({
            icon: 'warning',
            title: intl.formatMessage(
              {id: 'ALERT.LABEL.SUCCESS_CANCEL_ITEM'},
              {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
            ),
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
        }
      })
  }

  const deleteItem = useMutation((id: ID) => deleteAttachment(id), {
    onSuccess: (data, id) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_CANCEL_ITEM'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASKATTACHEMENT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      setLocalAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
    },
  })

  return (
    <>
      <form id='kt_modal_add_contract_form' className='form' onSubmit={handleCreate} noValidate>
        <div className='row align-items-center mb-4'>
          <div className='col-12 col-md-6 d-flex justify-content-end pe-0'>
            <div className='mb-4'>
              <label className='required fw-bold mb-1'>
                {intl.formatMessage({id: 'FORM.INPUT.NAME.NEW_FILE'})}
              </label>

              <input
                type='file'
                name='file'
                onKeyPress={(e) => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                id='file'
                className='form-control form-control-solid py-2  rounded fw-normal'
                accept={TASK_ATTACHMENT_ACCEPT}
                onChange={handleCreate}
                //   ref={ref}
              />
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12 p-0 shadow bg-body fs-6'>
            <div className='min-h-225px overflow-scroll'>
              {/* IT IS HIDDEN UNTIL WE HAVE CONTRACTS TO PROJECT */}
              <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                <thead>
                  <tr className='text-start text-gray-400 fw-bold fs-8 text-uppercase gs-0'>
                    <th className='min-w-300px p-5 shadow-sm '>
                      {intl.formatMessage({id: 'ATTACHEMENT.TITLE'})}
                    </th>
                  </tr>
                </thead>
                {localAttachments.map((rs) => {
                  return (
                    <tbody className='text-gray-600 fw-bold' key={rs.id}>
                      <tr>
                        <td colSpan={1}>
                          <div className='d-flex align-items-center px-4'>
                            <KTSVG
                              path='/media/icons/duotune/files/fil003.svg'
                              className='svg-icon svg-icon-2x svg-icon-primary me-4'
                            />
                            <a href={rs.file} target='_blank'>
                              {rs.file_name}
                            </a>
                          </div>
                        </td>
                        <td colSpan={1}>
                          <button type='button' className='btn' onClick={() => deleta(rs.id)}>
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon svg-icon-2x svg-icon-primary me-4'
                            />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  )
                })}
              </table>
            </div>
          </div>
        </div>
      </form>
      {formik.isSubmitting}
    </>
  )
}
